import classNames from "classnames";
import React from "react";
import {
  SimpleInfoAlert,
  SimpleInfoAlertType
} from "../../../../Shared/SimpleInfoAlert/React";
import { NewTabIcon } from "../../components/shared/newTabIcon";
import { useTranslationContext } from "../../context";
import { ProvetDepartment } from "../../types";

/**
 * Note shown on clinics that don't book in this form, in the clinic list and the map popover:
 * "Separate booking page", as a compact info alert (a tag the height of the name's line).
 * It's inline (spans only), so it stays part of the row link's accessible name.
 */
export const SeparateBookingNote = ({ className }: { className?: string }) => {
  const { translations } = useTranslationContext();

  return (
    <span
      className={classNames("flex max-w-full", className)}
      data-online-booking-separate-booking-note
    >
      <SimpleInfoAlert compact type={SimpleInfoAlertType.Info} className="min-w-0">
        {translations?.separateBookingPageNote}
      </SimpleInfoAlert>
    </span>
  );
};

interface OldBookingListItemProps {
  department: ProvetDepartment;
  /** Undefined when the old booking domain isn't configured: the row is then not a link */
  oldBookingUrl?: string;
}

/**
 * Clinic list row for a clinic that isn't on the "Active Provet departments" list.
 * The whole row is a link to the clinic's old booking page, which opens in a new tab.
 * It sits in the list in its normal place, next to the selectable clinics.
 */
export const OldBookingListItem = ({
  department,
  oldBookingUrl
}: OldBookingListItemProps) => {
  const { translations } = useTranslationContext();

  const content = (
    <>
      {/* Filled where bookable rows have their radio, so this row doesn't look selectable */}
      <span
        className={classNames(
          "flex items-center px-md py-sm",
          "rounded-tl-br-sm aspect-square border border-neutral-200 bg-neutral-200",
          "text-dark-blue [&>i]:pl-0"
        )}
      >
        {/* Decorative here; "opens in new window" is read after the clinic name instead */}
        <NewTabIcon screenReaderText="" />
      </span>
      <span className="flex flex-col gap-xxs grow transition-short-in-out">
        {department.address_info?.city && (
          <span className="uppercase font-distinct-bold font-guardian text-distinct-xs md:text-distinct-sm text-t-neutral-700">
            {department.address_info.city}
          </span>
        )}
        {/* Name and note share a line; the note drops below the name when there isn't room */}
        <span className="flex flex-wrap items-center gap-x-xs gap-y-xxs">
          <span className="text-md md:text-lg font-t-semi-bold text-dark-blue group-hover/oldbooking:underline">
            {department.name}
          </span>
          {oldBookingUrl && <span className="sr-only">{translations?.newWindow}</span>}
          <SeparateBookingNote />
        </span>
      </span>
    </>
  );

  const className = classNames(
    "relative overflow-hidden group/oldbooking",
    "flex gap-xs w-full items-center transition-short-in-out",
    "rounded-sm p-xs"
  );

  // Without a configured old booking domain there is nowhere to link to
  if (!oldBookingUrl) {
    return (
      <div className={className} data-online-booking-department-id={department.id}>
        {content}
      </div>
    );
  }

  return (
    <a
      href={oldBookingUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={classNames(className, "focus-outline")}
      data-online-booking-action="goToOldBooking"
      data-online-booking-department-id={department.id}
    >
      {content}
    </a>
  );
};

export default OldBookingListItem;
